import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTheme } from '../hooks/useTheme';
import {
  ZapIcon,
  TwitterIcon,
  GithubIcon,
  LinkedinIcon,
  MailIcon,
  YoutubeIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ShieldCheckIcon,
  LockIcon,
  AwardIcon,
  HeartIcon,
  Sparkles,
  Globe,
  Code,
  Zap,
  TrendingUp
} from 'lucide-react';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Animated gradient mesh background
function GradientMesh({ isDark }) {
  return (
    <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
      <div
        className="absolute -top-40 -left-40 w-80 h-80 rounded-full blur-lg "
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6, 182, 212, 0.12) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute -top-40 -left-40 w-80 h-80 rounded-full blur-lg"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute -top-40 -left-40 w-80 h-80 rounded-full blur-lg"
        style={{
          background: isDark
            ? 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
        }}
      />
    </div>
  );
}

// Premium Footer Link
function FooterLink({ children, href = '#' }: { children: React.ReactNode; href?: string }) {
  const { isDark } = useTheme();

  return (
    <li className="group relative">
      <a
        href={href}
        className={`relative text-base font-medium transition-all duration-300 inline-flex items-center gap-1.5 ${
          isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <span className="relative">
          {children}
          <span
            className={`absolute -bottom-1 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${
              isDark
                ? 'bg-gradient-to-r from-cyan-400 to-blue-400'
                : 'bg-gradient-to-r from-cyan-600 to-blue-600'
            }`}
          />
        </span>
        <ArrowRightIcon className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
      </a>
    </li>
  );
}


// Premium Social Icon
function SocialIcon({ icon: IconComponent, label, href = '#' }: { icon: React.ElementType; label: string; href?: string }) {
  const { isDark } = useTheme();

  return (
    <a
      href={href}
      className={`group relative p-3 rounded-xl border-2 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 ${
        isDark
          ? 'text-slate-500 bg-white/[0.02] border-white/[0.08] hover:text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/30'
          : 'text-slate-500 bg-white border-slate-200 hover:text-cyan-600 hover:bg-cyan-50 hover:border-cyan-400 hover:shadow-xl hover:shadow-cyan-500/20'
      }`}
      aria-label={label}
    >
      <IconComponent className="w-5 h-5 relative z-10" />
      <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-md ${
        isDark ? 'bg-cyan-500/20' : 'bg-cyan-400/20'
      }`} />
    </a>
  );
}

// Stats Counter
function StatsCounter({ value, label, icon: Icon }) {
  const { isDark } = useTheme();
  const numberRef = useRef(null);

  useEffect(() => {
    const el = numberRef.current;
    if (!el) return;

    const obj = { val: 0 };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;

        gsap.to(obj, {
          val: parseInt(value),
          duration: 4.2,
          ease: "power2.out",
          onUpdate: () => {
            el.textContent =
              Math.floor(obj.val).toLocaleString() + "+";
          }
        });

        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div className="text-center">
      <div className="flex items-center justify-center mb-2">
        <Icon className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
      </div>

      <div
        ref={numberRef}
        className={`text-3xl font-bold mb-1 ${
          isDark
            ? 'text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-300'
            : 'text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600'
        }`}
      >
        0+
      </div>

      <div className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
        {label}
      </div>
    </div>
  );
}


export function Footer() {
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const newsletterRef = useRef(null);
  const statsRef = useRef(null);
  const linksRef = useRef(null);

  useEffect(() => {
  const ctx = gsap.context(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: newsletterRef.current,
        start: 'top 85%',
        toggleActions: 'play none none none',
        once: true 
      }
    });

    tl.from(newsletterRef.current, {
      opacity: 0,
      y: 40,
      duration: 0.4,
      ease: "power2.out"
    });

    tl.from(statsRef.current?.children || [], {
      opacity: 0,
      y: 30,
      stagger: 0.15,
      duration: 0.4,
      ease: "power2.out"
    }, "-=0.4");

    tl.from(linksRef.current?.children || [], {
      opacity: 0,
      y: 20,
      stagger: 0.08,
      duration: 0.4,
      ease: "power2.out"
    }, "-=0.3");

  });

  return () => ctx.revert();
}, []);


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative">
      <GradientMesh isDark={isDark} />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Newsletter Section */}
      <div id="contact" className="relative py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div ref={newsletterRef}>
            <div
              className={`relative rounded-3xl overflow-hidden border ${
                isDark
                  ? 'bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-800/95 border-white/[0.1] shadow-2xl shadow-black/40'
                  : 'bg-gradient-to-br from-white via-slate-50/50 to-white border-slate-200/60 shadow-2xl shadow-slate-900/10'
              }`}
              style={{ backdropFilter: 'blur(4px)',willChange: 'transform' }}
            >
              <div className="absolute inset-0 rounded-2xl opacity-0 hover:opacity-0.3 transition-opacity duration-700 pointer-events-none">
                <div className={`absolute inset-0 rounded-3xl ${
                  isDark
                    ? 'bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-violet-500/20'
                    : 'bg-gradient-to-r from-cyan-400/20 via-blue-400/20 to-violet-400/20'
                } blur-xl`} />
              </div>

              <div className="relative px-8 py-12 lg:px-16 lg:py-16">
                {/* Stats Row */}
                <div ref={statsRef} className={`grid grid-cols-3 gap-8 mb-12 pb-12 border-b ${
                  isDark ? 'border-white/[0.08]' : 'border-slate-200'
                }`}>
                  <StatsCounter value="12000" label="Businesses" icon={Globe} />
                  <StatsCounter value="135" label="Countries" icon={TrendingUp} />
                  <StatsCounter value="5000" label="Developers" icon={Code} />
                </div>

                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 will-change-transform">
                  <div className="max-w-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-xl ${
                        isDark ? 'bg-cyan-500/10' : 'bg-cyan-100'
                      }`}>
                        <Sparkles className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                      </div>
                      <h3 className={`text-1xl lg:text-3xl font-bold ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}>
                        Join the 1401 newsletter
                      </h3>
                    </div>
                    <p className={`text-base leading-relaxed ${
                      isDark ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      Get product updates, industry insights, and payment tech trends delivered to your inbox.
                      <span className={`block mt-2 text-sm font-medium ${
                        isDark ? 'text-cyan-400' : 'text-cyan-600'
                      }`}>
                        Join 10,000+ subscribers
                      </span>
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row w-full lg:w-auto gap-3">
                    <div className="relative flex-1 lg:w-96">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className={`w-full px-6 py-4 rounded-xl text-sm border-2 transition-all duration-300 outline-none ${
                          isDark
                            ? 'bg-white/[0.05] border-white/[0.1] text-white placeholder-slate-500 focus:bg-white/[0.08] focus:border-cyan-500/50 focus:shadow-lg focus:shadow-cyan-500/20'
                            : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-cyan-400 focus:shadow-lg focus:shadow-cyan-500/10'
                        }`}
                      />
                      <div className={`absolute inset-0 rounded-xl opacity-0 focus-within:opacity-100 transition-opacity duration-300 -z-10 blur-xl ${
                        isDark ? 'bg-cyan-500/20' : 'bg-cyan-400/20'
                      }`} />
                    </div>

                    <button
                      className={`group relative px-8 py-4 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden ${
                        isDark
                          ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 text-white shadow-xl shadow-cyan-500/40'
                          : 'bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600 text-white shadow-xl shadow-cyan-600/40'
                      }`}
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        Subscribe
                        <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </span>
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Line */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="flex items-center gap-4">
          <div className={`h-px flex-1 ${
            isDark ? 'bg-gradient-to-r from-transparent via-white/[0.1] to-white/[0.1]' : 'bg-gradient-to-r from-transparent via-slate-200 to-slate-200'
          }`} />
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                className={`w-1 h-1 rounded-full ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`}
              />
            ))}
          </div>
          <div className={`h-px flex-1 ${
            isDark ? 'bg-gradient-to-l from-transparent via-white/[0.1] to-white/[0.1]' : 'bg-gradient-to-l from-transparent via-slate-200 to-slate-200'
          }`} />
        </div>
      </div>

      {/* Links Grid */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          <div ref={linksRef} className="grid grid-cols-2 md:grid-cols-6 gap-12 lg:gap-16">
            <div className="col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-16 h-16 relative rounded-lg overflow-hidden ${
  isDark
    ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30'
    : 'bg-gradient-to-br from-cyan-100 to-blue-100 border-cyan-300'
}`}>
  <img 
    src="/Media.png"       
    alt="My Icon" 
    className="w-full h-full object-contain"      
    style={{ 
      transition: 'transform 0.2s ease, filter 0.2s ease',
      filter: isDark 
        ? 'drop-shadow(0 0 8px rgba(6, 182, 212, 0.4))' 
        : 'drop-shadow(0 0 8px rgba(0, 128, 255, 0.4))'  
    }}
  />
</div>
                <span className={`text-2xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  14
                  <span className={
                    isDark
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400'
                      : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600'
                  }>01</span>
                </span>
              </div>
              <p className={`text-sm leading-relaxed mb-8 max-w-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Payment infrastructure for the internet. Engineered for developers, trusted by businesses worldwide.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <SocialIcon icon={TwitterIcon} label="Twitter" />
                <SocialIcon icon={GithubIcon} label="GitHub" />
                <SocialIcon icon={LinkedinIcon} label="LinkedIn" />
                <SocialIcon icon={YoutubeIcon} label="YouTube" />
              </div>
            </div>

            <div>
              <h4 className={`text-s font-bold uppercase tracking-wider mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Product</h4>
              <ul className="space-y-3.5">
                <FooterLink>Payments</FooterLink>
                <FooterLink>Billing</FooterLink>
                <FooterLink>Connect</FooterLink>
                <FooterLink>Invoicing</FooterLink>
                <FooterLink>Checkout</FooterLink>
                <FooterLink>Terminal</FooterLink>
              </ul>
            </div>

            <div>
              <h4 className={`text-s font-bold uppercase tracking-wider mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Developers</h4>
              <ul className="space-y-3.5">
                <FooterLink>Documentation</FooterLink>
                <FooterLink>API Reference</FooterLink>
                <FooterLink>Libraries</FooterLink>
                <FooterLink>Webhooks</FooterLink>
                <FooterLink>Changelog</FooterLink>
                <FooterLink>Community</FooterLink>
              </ul>
            </div>

            <div>
              <h4 className={`text-s font-bold uppercase tracking-wider mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Company</h4>
              <ul className="space-y-3.5">
                <FooterLink>About</FooterLink>
                <FooterLink>Customers</FooterLink>
                <FooterLink>Blog</FooterLink>
                <FooterLink>Careers</FooterLink>
                <FooterLink>Partners</FooterLink>
                <FooterLink>Contact</FooterLink>
              </ul>
            </div>

            <div>
              <h4 className={`text-s font-bold uppercase tracking-wider mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Legal</h4>
              <ul className="space-y-3.5">
                <FooterLink>Privacy</FooterLink>
                <FooterLink>Terms</FooterLink>
                <FooterLink>Security</FooterLink>
                <FooterLink>Compliance</FooterLink>
                <FooterLink>Licenses</FooterLink>
                <FooterLink>Cookies</FooterLink>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={`border-t ${isDark ? 'border-white/[0.08]' : 'border-slate-200'}`}>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className={`text-sm flex items-center gap-2 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                © 2026 1401, Inc.
              </div>

              <div className="flex items-center gap-6">
                {[
                  { icon: ShieldCheckIcon, text: 'PCI Level 1' },
                  { icon: LockIcon, text: 'SOC 2' },
                  { icon: AwardIcon, text: 'ISO 27001' }
                ].map((badge, i) => (
                  <div
                    key={i}
                    className={`group flex items-center gap-2 text-xs font-medium transition-all duration-300 hover:scale-105 cursor-pointer ${
                      isDark ? 'text-slate-500 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg border transition-all duration-300 ${
                      isDark
                        ? 'bg-white/[0.03] border-white/[0.08] group-hover:bg-cyan-500/10 group-hover:border-cyan-500/30'
                        : 'bg-slate-50 border-slate-200 group-hover:bg-cyan-50 group-hover:border-cyan-300'
                    }`}>
                      <badge.icon className="w-4 h-4" />
                    </div>
                    {badge.text}
                  </div>
                ))}
              </div>

              <button
                onClick={scrollToTop}
                className={`group relative p-3 rounded-xl border-2 transition-all duration-300 hover:scale-110 hover:-translate-y-0.5 ${
                  isDark
                    ? 'bg-white/[0.02] border-white/[0.08] text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/30'
                    : 'bg-white border-slate-200 text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 hover:border-cyan-400 hover:shadow-xl hover:shadow-cyan-500/20'
                }`}
                aria-label="Back to top"
              >
                <ArrowUpIcon className="w-5 h-5" />
                <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-md -z-10 ${
                  isDark ? 'bg-cyan-500/30' : 'bg-cyan-400/30'
                }`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}